import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MysteryType } from '../types';

// Onboarding version - increment this to reset onboarding for all users
const ONBOARDING_VERSION = 1;
const STORAGE_KEY = 'onboarding-state';

// Onboarding state types
export type OnboardingIntent = '54-day-novena' | 'daily-rosary' | 'explore-novenas' | 'browsing';

export type OnboardingStep =
  | 'intent-discovery'      // Screen 1
  | 'novena-setup'          // Screen 2A
  | 'rosary-setup'          // Screen 2B
  | 'novena-explorer'       // Screen 2C
  | 'feature-preview'       // Screen 3
  | 'first-prayer'          // Screen 4
  | 'post-prayer-journal'   // Screen 5
  | 'progress-celebration'  // Screen 6
  | 'completed';

export interface OnboardingSetupData {
  // For 54-day novena
  novenaIntention?: string;

  // For daily rosary
  selectedMystery?: MysteryType;
  mysteryPreference?: 'rotate' | 'focus' | 'choose';
  focusedMysteries?: MysteryType[];

  // For traditional novenas
  selectedNovenaId?: string;

  // Common
  reminderTime?: string; // HH:MM format
  enableReminders?: boolean;
  reminderBefore?: boolean; // 15 min before

  // Journal entries
  prePrayerMood?: string;
  prePrayerNote?: string;
  postPrayerMood?: string;
  postPrayerNote?: string;
}

interface OnboardingState {
  version: number;
  isOnboarding: boolean;
  currentStep: OnboardingStep;
  hasCompletedOnboarding: boolean;
  selectedIntent: OnboardingIntent | null;
  setupData: OnboardingSetupData;
  onboardingStartTime: Date | null;
  onboardingCompletionTime: Date | null;
  stepHistory: OnboardingStep[];
}

interface OnboardingContextType extends OnboardingState {
  // Flow control
  startOnboarding: () => void;
  completeOnboarding: (callback?: () => void) => void;
  skipOnboarding: () => void;

  // Step navigation
  setStep: (step: OnboardingStep) => void;
  nextStep: () => void;
  previousStep: () => void;
  canGoBack: () => boolean;

  // Data management
  setIntent: (intent: OnboardingIntent) => void;
  updateSetupData: (data: Partial<OnboardingSetupData>) => void;

  // Helpers
  getStepNumber: () => number;
  getTotalSteps: () => number;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// Step sequence based on intent
const getStepSequence = (intent: OnboardingIntent | null): OnboardingStep[] => {
  const baseSequence: OnboardingStep[] = ['feature-preview', 'intent-discovery'];

  if (!intent) return baseSequence;

  switch (intent) {
    case '54-day-novena':
      return [...baseSequence, 'novena-setup', 'completed'];
    case 'daily-rosary':
      return [...baseSequence, 'rosary-setup', 'completed'];
    case 'explore-novenas':
      return [...baseSequence, 'novena-explorer', 'completed'];
    case 'browsing':
      return [...baseSequence, 'completed'];
    default:
      return baseSequence;
  }
};

const defaultState: OnboardingState = {
  version: ONBOARDING_VERSION,
  isOnboarding: false,
  currentStep: 'feature-preview',
  hasCompletedOnboarding: false,
  selectedIntent: null,
  setupData: {},
  onboardingStartTime: null,
  onboardingCompletionTime: null,
  stepHistory: [],
};

export const OnboardingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<OnboardingState>(() => {
    // Load from localStorage on init
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);

        // Check if version matches - if not, reset onboarding
        if (parsed.version !== ONBOARDING_VERSION) {
          console.log(`Onboarding version mismatch (stored: ${parsed.version}, current: ${ONBOARDING_VERSION}). Resetting onboarding.`);
          return defaultState;
        }

        // Convert date strings back to Date objects and ensure stepHistory exists
        return {
          ...parsed,
          onboardingStartTime: parsed.onboardingStartTime ? new Date(parsed.onboardingStartTime) : null,
          onboardingCompletionTime: parsed.onboardingCompletionTime ? new Date(parsed.onboardingCompletionTime) : null,
          stepHistory: parsed.stepHistory || [],
        };
      }
    } catch (error) {
      console.error('Failed to load onboarding state:', error);
    }
    return defaultState;
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save onboarding state:', error);
    }
  }, [state]);

  const startOnboarding = () => {
    setState(prev => ({
      ...prev,
      isOnboarding: true,
      currentStep: 'feature-preview',
      onboardingStartTime: new Date(),
      hasCompletedOnboarding: false,
    }));
  };

  const completeOnboarding = (callback?: () => void) => {
    setState(prev => ({
      ...prev,
      isOnboarding: false,
      currentStep: 'completed',
      hasCompletedOnboarding: true,
      onboardingCompletionTime: new Date(),
    }));

    // Execute callback after state update if provided
    if (callback) {
      // Use setTimeout to ensure state update completes first
      setTimeout(callback, 0);
    }
  };

  const skipOnboarding = () => {
    setState(prev => ({
      ...prev,
      isOnboarding: false,
      hasCompletedOnboarding: true,
      onboardingCompletionTime: new Date(),
    }));
  };

  const setStep = (step: OnboardingStep) => {
    setState(prev => ({
      ...prev,
      currentStep: step,
      stepHistory: [...prev.stepHistory, prev.currentStep],
    }));
  };

  const nextStep = () => {
    const sequence = getStepSequence(state.selectedIntent);
    const currentIndex = sequence.indexOf(state.currentStep);

    if (currentIndex < sequence.length - 1) {
      const nextStep = sequence[currentIndex + 1];
      setState(prev => ({
        ...prev,
        currentStep: nextStep,
        stepHistory: [...prev.stepHistory, prev.currentStep],
      }));

      // Auto-complete if reached 'completed' step
      if (nextStep === 'completed') {
        completeOnboarding();
      }
    }
  };

  const previousStep = () => {
    if (state.stepHistory.length > 0) {
      const previousStep = state.stepHistory[state.stepHistory.length - 1];
      const newHistory = state.stepHistory.slice(0, -1);
      setState(prev => ({
        ...prev,
        currentStep: previousStep,
        stepHistory: newHistory,
      }));
    }
  };

  const canGoBack = (): boolean => {
    return state.stepHistory.length > 0 && state.currentStep !== 'feature-preview';
  };

  const setIntent = (intent: OnboardingIntent) => {
    setState(prev => ({ ...prev, selectedIntent: intent }));
  };

  const updateSetupData = (data: Partial<OnboardingSetupData>) => {
    setState(prev => ({
      ...prev,
      setupData: { ...prev.setupData, ...data },
    }));
  };

  const getStepNumber = (): number => {
    const sequence = getStepSequence(state.selectedIntent);
    return sequence.indexOf(state.currentStep) + 1;
  };

  const getTotalSteps = (): number => {
    const sequence = getStepSequence(state.selectedIntent);
    // Don't count 'completed' in total
    return sequence.filter(s => s !== 'completed').length;
  };

  const value: OnboardingContextType = {
    ...state,
    startOnboarding,
    completeOnboarding,
    skipOnboarding,
    setStep,
    nextStep,
    previousStep,
    canGoBack,
    setIntent,
    updateSetupData,
    getStepNumber,
    getTotalSteps,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};